export const getDisplayTag = (tag) => {
  const paddedTag = tag.toString().padStart(4, "0");
  return "#" + paddedTag;
};

/**
 * Calculates the highest contrast font color based on the given background
 * color and chooses either the light or dark color.
 *
 * The calculation is based on the w3.org recommendation for relative luminance.
 * See https://www.w3.org/TR/WCAG20/#relativeluminancedef and
 * https://stackoverflow.com/a/3943023 for implementation step2.
 */
export const pickHighestContrastColor = (bgColor, lightColor, darkColor) => {
  const color = bgColor.charAt(0) === "#" ? bgColor.substring(1, 7) : bgColor;
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);
  const srgbColors = [r / 255, g / 255, b / 255];
  const c = srgbColors.map((col) => {
    if (col <= 0.03928) {
      return col / 12.92;
    }

    return Math.pow((col + 0.055) / 1.055, 2.4);
  });

  const relativeLuminance = 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];

  return relativeLuminance > 0.179 ? darkColor : lightColor;
};

/**
 * Selects the color with the highest contrast against the given background color
 * based on a relaxed luminance threshold.
 * Do NOT use if possible, as it is not WCAG compliant.
 *
 * @param {string} bgColor - The background color in hexadecimal format (e.g., `#RRGGBB`).
 * @param {string} lightColor - The light color option to be chosen if the background is dark.
 * @param {string} darkColor - The dark color option to be chosen if the background is light.
 * @returns {string} The lightColor or darkColor, whichever has higher relaxed contrast with the background.
 */
export const pickHighestContrastColorRelaxed = (
  bgColor,
  lightColor,
  darkColor,
) => {
  const color = bgColor.charAt(0) === "#" ? bgColor.substring(1, 7) : bgColor;
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);
  const srgbColors = [r / 255, g / 255, b / 255]; // Normalize RGB to 0-1 range

  // Find out perceived "brightness" per color channel
  const c = srgbColors.map((col) => {
    if (col <= 0.03928) {
      return col / 12.92;
    }

    return Math.pow((col + 0.055) / 1.055, 2.4);
  });

  // Find out total brightness of the color
  const relativeLuminance = 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];

  // We go against WCAG compliance with this threshold. According to WCAG, we need 0.18-0.2.
  return relativeLuminance > 0.6 ? darkColor : lightColor;
};

/**
 * Convert a hexadecimal color code to RGBA format.
 *
 * @param {string} hex - The hexadecimal color code to be converted. Should be in the format "#RRGGBB".
 * @param {number} opacity - The opacity value for the RGBA color. Should be a value between 0 and 1. Defaults to 1.
 * @returns {string} - The RGBA color code in the format "rgba(r, g, b, opacity)".
 */
export function hexToRGBA(hex, opacity = 1) {
  if (Array.isArray(hex)) hex = hex[0];
  if (!hex) hex = "000";
  if (hex.startsWith("#")) hex = hex.slice(1);
  if (hex.length === 3) hex = `${hex}${hex}`;
  if (hex.length > 6) hex = hex.slice(0, 6);

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Converts the provided values to pixels and returns their sum.
 *
 * @param {...string} values - The values to convert to pixels.
 * Each value should be a string containing a number followed by either "rem" or "px" to indicate the unit of measurement.
 *
 * @return {number} - The sum of the converted values in pixels.
 *
 * @throws {Error} - If a value does not have a valid unit of measurement ("rem" or "px").
 *
 */
export function addToPx(...values) {
  const baseFontSize = 16;
  return values
    .map((value) => {
      const num = parseFloat(value);
      if (value.includes("rem")) {
        return num * baseFontSize;
      } else if (value.includes("px")) {
        return num;
      } else {
        throw Error(`No type provided for the given value ${value}`);
      }
    })
    .reduce((total, num) => total + num, 0);
}
